import React from "react";
import "../styles/PrintPage.css";
import { Box, Button, Text, Stack, Actions } from "braid-design-system";
import { useLocation } from "react-router-dom";
import apiClient from "../clients/apiClient";
import Loading from "../components/Loading";
import { formatCurrency } from "../utils/currencyUtils";
import { formatPrintDate } from "../utils/dateUtils";

interface FDReportAccount {
    id: number;
    ref_account_number: string;
    customer_number: string;
    customer_name: string;
    period: number;
    interest_rate: number;
    balance: number;
    interest: number;
}

interface FDReportGroup {
    account_type_name: string;
    accounts: FDReportAccount[];
    sub_total_balance: number;
    sub_total_interest: number;
    sub_total_count: number;
}

interface FDReportData {
    institute_name: string;
    branch_name: string;
    working_date: string | null;
    groups: FDReportGroup[];
    grand_total_balance: number;
    grand_total_interest: number;
    grand_total_count: number;
    active_filters: Record<string, string> | null;
}

const FDManagementPrint: React.FC = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const searchTerm = queryParams.get("search_term") || "";
    const filterOption = queryParams.get("filter_option") || "";
    const filterRenewDate = queryParams.get("filter_renew_date") || "";

    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [reportData, setReportData] = React.useState<FDReportData | null>(null);

    React.useEffect(() => {
        const fetchReportData = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await apiClient.get("/fd-update/report-data", {
                    params: {
                        search_term: searchTerm,
                        filter_option: filterOption,
                        filter_renew_date: filterRenewDate,
                    },
                });
                if (response.data?.data) {
                    setReportData(response.data.data);
                } else {
                    setError("Failed to load report data.");
                }
            } catch (err: any) {
                // eslint-disable-next-line no-console
                console.error("Error fetching FD report data:", err);
                setError(err.response?.data?.message || "Failed to load report data.");
            } finally {
                setLoading(false);
            }
        };

        fetchReportData();
    }, [searchTerm, filterOption, filterRenewDate]);

    const handlePrint = () => {
        window.print();
    };

    const handleClose = () => {
        window.close();
    };

    if (loading) {
        return <Loading message="Loading print report details, please wait..." />;
    }

    if (error) {
        return (
            <Box padding="large" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
                <Stack space="medium">
                    <Text size="large" tone="critical" weight="strong">Error</Text>
                    <Text>{error}</Text>
                    <Button onClick={handleClose} size="small">Close Page</Button>
                </Stack>
            </Box>
        );
    }

    if (!reportData || reportData.groups.length === 0) {
        return (
            <Box padding="large" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
                <Stack space="medium">
                    <Text size="large" tone="secondary" weight="strong">No Records Found</Text>
                    <Text>There are no fixed deposit accounts matching the selected criteria.</Text>
                    <Button onClick={handleClose} size="small">Close Page</Button>
                </Stack>
            </Box>
        );
    }

    const renderActiveFilters = () => {
        if (!reportData.active_filters || Object.keys(reportData.active_filters).length === 0) {
            return null;
        }

        const filterItems: string[] = [];
        if (reportData.active_filters.search_term) {
            filterItems.push(`Search: "${reportData.active_filters.search_term}"`);
        }
        if (reportData.active_filters.filter_option) {
            filterItems.push(`Maturity Option: ${reportData.active_filters.filter_option}`);
        }
        if (reportData.active_filters.filter_renew_date) {
            filterItems.push(`Renew Date: ${reportData.active_filters.filter_renew_date}`);
        }

        return (
            <div className="report-filters">
                Filtered by: {filterItems.join(" | ")}
            </div>
        );
    };

    return (
        <Box style={{ position: "relative", width: "100%", backgroundColor: "#f6f8fa", minHeight: "100vh", paddingBottom: "40px" }}>
            {/* Styles inject */}
            <style dangerouslySetInnerHTML={{
                __html: `
                .report-container {
                    max-width: 900px;
                    margin: 0 auto;
                    padding: 30px;
                    background-color: #fff;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06);
                    font-family: 'Noto Serif', 'Noto Serif Sinhala', 'Times New Roman', serif;
                    color: #000;
                    margin-top: 20px;
                }
                .report-header {
                    text-align: center;
                    margin-bottom: 25px;
                    border-bottom: 2px solid #000;
                    padding-bottom: 15px;
                }
                .report-header h2 {
                    font-size: 19px;
                    margin: 0 0 6px 0;
                    font-weight: bold;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                .report-header h3 {
                    font-size: 14px;
                    margin: 0 0 6px 0;
                    font-weight: 600;
                }
                .report-header .report-date {
                    font-size: 13px;
                    font-weight: bold;
                    margin-top: 8px;
                }
                .report-header .report-filters {
                    font-size: 11px;
                    color: #444;
                    margin-top: 6px;
                    font-style: italic;
                    background-color: #f8f9fa;
                    display: inline-block;
                    padding: 3px 10px;
                    border-radius: 4px;
                    border: 1px dashed #ccc;
                }
                .group-section {
                    margin-bottom: 25px;
                }
                .group-title {
                    font-size: 13px;
                    font-weight: bold;
                    margin: 0 0 8px 0;
                    text-align: left;
                }
                .report-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 11px;
                }
                .report-table th, .report-table td {
                    border: 1px solid #000;
                    padding: 5px 6px;
                    vertical-align: middle;
                }
                .report-table th {
                    background-color: #f2f2f2;
                    font-weight: bold;
                    text-align: center;
                }
                .report-table td.center-align {
                    text-align: center;
                }
                .report-table td.right-align {
                    text-align: right;
                }
                .report-table tr.subtotal-row td {
                    font-weight: bold;
                    background-color: #fcfcfc;
                    border-top: 1px double #000;
                    border-bottom: 1px double #000;
                }
                .grand-total-section {
                    margin-top: 30px;
                    border-top: 2px solid #000;
                    padding-top: 10px;
                }
                .grand-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 11px;
                }
                .grand-table td {
                    border: 1px solid #000;
                    padding: 6px;
                    font-weight: bold;
                    background-color: #f5f5f5;
                }
                .grand-table td.right-align {
                    text-align: right;
                }

                @media print {
                    body {
                        background-color: #fff !important;
                    }
                    .report-container {
                        box-shadow: none !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        max-width: 100% !important;
                        width: 100% !important;
                    }
                    .no-print {
                        display: none !important;
                    }
                }
            ` }} />

            {/* Toolbar */}
            <Box className="no-print" padding="medium" display="flex" justifyContent={"flexEnd"}>
                <Actions>
                    <Button onClick={handleClose} variant="soft" size="small">Close</Button>
                    <Button onClick={handlePrint} variant="soft" size="small" tone="brandAccent">Print Report</Button>
                </Actions>
            </Box>

            {/* Report Content */}
            <div id="print-page" className="report-container">
                <div className="report-header">
                    <h2>{reportData.institute_name}</h2>
                    <h3>Branch : {reportData.branch_name}</h3>
                    <div className="report-date">
                        Customer Fixed Deposit Detail for the Date of {reportData.working_date ? formatPrintDate(reportData.working_date) : ""}
                    </div>
                    {renderActiveFilters()}
                </div>

                {reportData.groups.map((group, groupIdx) => (
                    <div className="group-section" key={groupIdx}>
                        <div className="group-title">{group.account_type_name}</div>
                        <table className="report-table">
                            <thead>
                                <tr>
                                    <th style={{ width: "5%" }}>No</th>
                                    <th style={{ width: "15%" }}>A/C No.</th>
                                    <th style={{ width: "45%" }}>Cus. No. & Customer Name</th>
                                    <th style={{ width: "8%" }}>Period</th>
                                    <th style={{ width: "7%" }}>%</th>
                                    <th style={{ width: "10%" }}>Balance</th>
                                    <th style={{ width: "10%" }}>Interest</th>
                                </tr>
                            </thead>
                            <tbody>
                                {group.accounts.map((acc, accIdx) => (
                                    <tr key={acc.id}>
                                        <td className="center-align">{String(accIdx + 1).padStart(2, "0")}</td>
                                        <td className="center-align">{acc.ref_account_number}</td>
                                        <td>{acc.customer_number} {acc.customer_name}</td>
                                        <td className="center-align">{acc.period}</td>
                                        <td className="center-align">{acc.interest_rate.toFixed(2)}</td>
                                        <td className="right-align">{formatCurrency(acc.balance)}</td>
                                        <td className="right-align">{formatCurrency(acc.interest)}</td>
                                    </tr>
                                ))}
                                <tr className="subtotal-row">
                                    <td colSpan={2}>Sub Total</td>
                                    <td style={{ fontWeight: "normal", fontStyle: "italic" }}>Total Accounts: {group.sub_total_count}</td>
                                    <td className="center-align">{group.sub_total_count}</td>
                                    <td className="center-align"></td>
                                    <td className="right-align">{formatCurrency(group.sub_total_balance)}</td>
                                    <td className="right-align">{formatCurrency(group.sub_total_interest)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                ))}

                {/* Grand Total */}
                <div className="grand-total-section">
                    <table className="grand-table">
                        <tbody>
                            <tr>
                                <td style={{ width: "20%" }}>Grand Total</td>
                                <td style={{ width: "45%", fontWeight: "normal", fontStyle: "italic" }}>Total Group Count: {reportData.groups.length} | Total Accounts: {reportData.grand_total_count}</td>
                                <td style={{ width: "15%", textAlign: "center" }}>{reportData.grand_total_count}</td>
                                <td style={{ width: "10%", textAlign: "right" }}>{formatCurrency(reportData.grand_total_balance)}</td>
                                <td style={{ width: "10%", textAlign: "right" }}>{formatCurrency(reportData.grand_total_interest)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </Box>
    );
};

export default FDManagementPrint;
